import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useMemo, useState } from 'react';
import { theme } from '../../design-system';
import { describeMediaError, permissionHelp } from '../../../../helpers/video/media';

// Small, self-contained status UI for the video tiles. Tiles range from ~100px thumbnails
// to full screen, so the overlay is a size container and drops detail on small tiles.

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Tile = styled.div`
  position: relative;
  isolation: isolate;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: ${(p) => (p.disableBorderRadius ? 0 : p.radius)}px;
  background-color: ${theme.colors.backgroundShade};
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  container-type: size;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.75rem;
  box-sizing: border-box;
  text-align: center;
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: ${(p) => (p.tone === 'dark' ? '#F1F5F9' : theme.colors.neutralDark)};
  background-color: ${(p) => (p.transparent ? 'transparent' : p.tone === 'dark' ? '#13161B' : theme.colors.backgroundShade)};
  overflow: auto;

  @container (max-width: 260px) {
    .tile-detail {
      display: none;
    }
  }
  @container (max-height: 180px) {
    .tile-detail {
      display: none;
    }
  }
  @container (max-width: 150px) {
    .tile-title {
      font-size: 0.65rem;
    }
    .tile-spinner {
      width: 16px;
      height: 16px;
    }
  }
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 0.875rem;
  line-height: 1.25;
`;

const Detail = styled.div`
  font-size: 0.75rem;
  line-height: 1.35;
  max-width: 32rem;
`;

const Code = styled.div`
  font-size: 0.625rem;
  color: ${theme.colors.neutralLight};
`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid ${theme.colors.primaryTint};
  border-top-color: ${theme.colors.primary};
  animation: ${spin} 0.9s linear infinite;
`;

const ActionButton = styled.button`
  margin-top: 0.25rem;
  padding: 0.4rem 1.1rem;
  border-radius: 4px;
  border: 1px solid ${theme.colors.primary};
  background-color: ${theme.colors.primary};
  color: #fff;
  font-size: 0.75rem;
  font-family: inherit;
  cursor: pointer;
  &:hover {
    box-shadow: ${theme.elevations.elevation1};
  }
`;

const Banner = styled.div`
  position: absolute;
  z-index: 3;
  left: 50%;
  transform: translateX(-50%);
  ${(p) => (p.bottom ? 'bottom: 0.5rem;' : 'top: 0.5rem;')}
  max-width: calc(100% - 1rem);
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.7rem;
  line-height: 1.3;
  color: ${(p) => (p.tone === 'error' ? '#fff' : theme.colors.neutralDark)};
  background-color: ${(p) => (p.tone === 'error' ? theme.colors.negativeDark : theme.colors.warning)};
  box-shadow: ${theme.elevations.elevation1};
  display: flex;
  gap: 0.4rem;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  button {
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
    padding: 0;
  }
`;

const BaseStatusCard = ({ title, detail, help, code, busy, action, onAction, transparent, testId, tone }) => (
  <Overlay transparent={transparent} tone={tone} data-testid={testId} role='status' aria-live='polite'>
    {busy && <Spinner className='tile-spinner' />}
    {title && <Title className='tile-title'>{title}</Title>}
    {detail && <Detail className='tile-detail'>{detail}</Detail>}
    {help && <Detail className='tile-detail'>{help}</Detail>}
    {code && <Code className='tile-detail'>{code}</Code>}
    {action && <ActionButton onClick={onAction}>{action}</ActionButton>}
  </Overlay>
);

const errorCode = (error) => (error ? [error.name, error.code].filter(Boolean).join(' ') : null);

// Status of the local camera tile. Returns null when the preview should simply be shown.
export const LocalTileStatus = ({ mediaStatus, mediaError, permissionState, hasVideo, isCameraEnabled, onRetry, tone }) => {
  const StatusCard = useMemo(() => (props) => <BaseStatusCard tone={tone} {...props} />, [tone]); // stable per tone
  switch (mediaStatus) {
    case 'idle':
      return hasVideo ? null : <StatusCard busy title='Starting camera…' testId='local-status-idle' />;
    case 'requesting-permission':
      if (permissionState === 'granted') return <StatusCard busy title='Starting camera…' testId='local-status-starting' />;
      return (
        <StatusCard
          busy
          title='Allow camera and microphone'
          detail='Your browser is asking for permission to use your camera and microphone. Choose "Allow" to join with video.'
          testId='local-status-requesting'
        />
      );
    case 'permission-denied':
      return (
        <StatusCard
          title='Camera access blocked'
          detail={describeMediaError(mediaError)}
          help={permissionHelp()}
          code={errorCode(mediaError)}
          action='Retry'
          onAction={onRetry}
          testId='local-status-denied'
        />
      );
    case 'no-device':
      return (
        <StatusCard
          title='No camera found'
          detail='Connect a camera (or check it is enabled), then press Retry.'
          code={errorCode(mediaError)}
          action='Retry'
          onAction={onRetry}
          testId='local-status-no-device'
        />
      );
    case 'device-in-use':
      return (
        <StatusCard
          title='Camera is busy'
          detail='Another app or browser tab is using the camera (e.g. Zoom, Teams, or this session open twice). Close it, then press Retry.'
          code={errorCode(mediaError)}
          action='Retry'
          onAction={onRetry}
          testId='local-status-in-use'
        />
      );
    case 'failed':
      return (
        <StatusCard
          title='Camera could not start'
          detail={mediaError?.name === 'TrackEnded' ? mediaError.message : describeMediaError(mediaError)}
          code={errorCode(mediaError)}
          action='Retry'
          onAction={onRetry}
          testId='local-status-failed'
        />
      );
    default:
      if (!hasVideo) return <StatusCard busy title='Starting camera…' testId='local-status-idle' />;
      if (!isCameraEnabled) return <StatusCard title='Your camera is off' testId='local-status-off' />;
      return null;
  }
};

// Status of the remote participant tile. Returns null when the remote video should be shown.
export const RemoteTileStatus = ({ connectionStatus, connectionError, participant, hasVideo, isVideoEnabled, onRetry, tone }) => {
  const StatusCard = useMemo(() => (props) => <BaseStatusCard tone={tone} {...props} />, [tone]); // stable per tone
  switch (connectionStatus) {
    case 'failed':
      return (
        <StatusCard
          title='Could not connect to the video session'
          detail={connectionError?.message}
          code={errorCode(connectionError)}
          action='Retry'
          onAction={onRetry}
          testId='remote-status-failed'
        />
      );
    case 'disconnected':
      return <StatusCard title='You are not connected to the video session' action='Rejoin' onAction={onRetry} testId='remote-status-disconnected' />;
    case 'idle':
    case 'connecting':
      return <StatusCard busy title='Connecting to the session…' testId='remote-status-connecting' />;
    default:
      break;
  }
  if (!participant) return <StatusCard busy title='Waiting for the other participant to join…' testId='remote-status-waiting' />;
  if (!hasVideo) {
    return participant.videoTracks?.size ? (
      <StatusCard busy title='Loading video…' testId='remote-status-loading' />
    ) : (
      <StatusCard title="The other participant's camera is not on" testId='remote-status-no-video' />
    );
  }
  if (!isVideoEnabled) return <StatusCard title="The other participant's camera is off" testId='remote-status-video-off' />;
  return null;
};

export const ReconnectingBanner = () => (
  <Banner role='status' data-testid='reconnecting-banner'>
    Connection lost, reconnecting…
  </Banner>
);

export const WarningBanner = ({ children, actionLabel, onAction }) => (
  <Banner bottom tone='error' role='alert' title={typeof children === 'string' ? children : undefined}>
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</span>
    {actionLabel && <button onClick={onAction}>{actionLabel}</button>}
  </Banner>
);

// --- Local media controls (mic / camera toggles and device selection) ---

const Controls = styled.div`
  position: absolute;
  z-index: 4;
  left: 50%;
  bottom: 0.5rem;
  transform: translateX(-50%);
  display: flex;
  gap: 0.4rem;
  align-items: center;
`;

const RoundButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  background-color: ${(p) => (p.off ? theme.colors.negativeDark : 'rgba(0, 0, 0, 0.45)')};
  &:hover {
    background-color: ${(p) => (p.off ? theme.colors.negativeDark : 'rgba(0, 0, 0, 0.6)')};
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const DevicePanel = styled.div`
  position: absolute;
  z-index: 5;
  left: 0.5rem;
  right: 0.5rem;
  bottom: 3rem;
  padding: 0.6rem;
  border-radius: 8px;
  background-color: #fff;
  box-shadow: ${theme.elevations.elevation3};
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.7rem;
  color: ${theme.colors.neutralDark};
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  label {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    font-weight: 700;
  }
  select {
    font: inherit;
    font-weight: 400;
    padding: 0.25rem;
    max-width: 100%;
  }
`;

const MicIcon = ({ off }) => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <rect x='9' y='2' width='6' height='12' rx='3' />
    <path d='M5 10v1a7 7 0 0 0 14 0v-1M12 18v4' />
    {off && <path d='M3 3l18 18' />}
  </svg>
);

const CamIcon = ({ off }) => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <rect x='2' y='6' width='14' height='12' rx='2' />
    <path d='M16 10l6-3v10l-6-3z' />
    {off && <path d='M3 3l18 18' />}
  </svg>
);

const GearIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <circle cx='12' cy='12' r='3' />
    <path d='M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z' />
  </svg>
);

export const LocalMediaControls = ({ isMicEnabled, isCameraEnabled, hasAudio, hasVideo, toggleMic, toggleCamera, devices, selectedDevices, switchDevice, deviceError }) => {
  const [showDevices, setShowDevices] = useState(false);
  const micOff = !hasAudio || !isMicEnabled;
  const camOff = !hasVideo || !isCameraEnabled;
  const canPick = devices.videoInputs.length > 0 || devices.audioInputs.length > 0;

  return (
    <>
      {showDevices && (
        <DevicePanel data-testid='device-panel'>
          {devices.videoInputs.length > 0 && (
            <label>
              Camera
              <select value={selectedDevices.video || ''} onChange={(e) => switchDevice('video', e.target.value || null)}>
                <option value=''>Default</option>
                {devices.videoInputs.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          {devices.audioInputs.length > 0 && (
            <label>
              Microphone
              <select value={selectedDevices.audio || ''} onChange={(e) => switchDevice('audio', e.target.value || null)}>
                <option value=''>Default</option>
                {devices.audioInputs.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          {deviceError && <span style={{ color: theme.colors.negativeDark }}>{describeMediaError(deviceError)}</span>}
        </DevicePanel>
      )}
      <Controls>
        <RoundButton off={micOff} onClick={toggleMic} title={micOff ? 'Turn microphone on' : 'Mute microphone'} aria-label={micOff ? 'Turn microphone on' : 'Mute microphone'}>
          <MicIcon off={micOff} />
        </RoundButton>
        <RoundButton off={camOff} onClick={toggleCamera} title={camOff ? 'Turn camera on' : 'Turn camera off'} aria-label={camOff ? 'Turn camera on' : 'Turn camera off'}>
          <CamIcon off={camOff} />
        </RoundButton>
        {canPick && (
          <RoundButton onClick={() => setShowDevices((s) => !s)} title='Choose camera and microphone' aria-label='Choose camera and microphone' aria-expanded={showDevices}>
            <GearIcon />
          </RoundButton>
        )}
      </Controls>
    </>
  );
};
